import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useViewAsId } from '@/lib/viewAs'
import type { KbArticle, KbCollection, KbFeatureFlag, KbListResponse, KbImageUploadResponse, KbRevision } from '@/types/kb'

// The reader queries carry the view-as lens in their key, so a lensed and an unlensed read never share a cache entry.
export const kbKeys = {
  all: ['kb'] as const,
  list: (params: Record<string, unknown>, asUser?: string) => [...kbKeys.all, 'list', params, asUser ?? null] as const,
  bySlug: (slug: string, asUser?: string) => [...kbKeys.all, 'slug', slug, asUser ?? null] as const,
  byId: (id: string) => [...kbKeys.all, 'id', id] as const,
  revisions: (id: string) => [...kbKeys.all, 'revisions', id] as const,
  revision: (id: string, revisionId: string) => [...kbKeys.all, 'revisions', id, revisionId] as const,
  featureFlags: () => [...kbKeys.all, 'feature-flags'] as const,
  collections: (asUser?: string) => [...kbKeys.all, 'collections', asUser ?? null] as const,
  collection: (slug: string, asUser?: string) => [...kbKeys.all, 'collections', slug, asUser ?? null] as const,
}

const lens = (asUser?: string) => (asUser ? { asUserId: asUser } : {})

export function useKbArticles(params: Record<string, unknown> = {}, { enabled = true }: { enabled?: boolean } = {}) {
  const asUser = useViewAsId()
  return useQuery({
    queryKey: kbKeys.list(params, asUser),
    queryFn: () => api<KbListResponse>('/kb-articles', { query: { ...params, ...lens(asUser) } }),
    enabled,
  })
}

export function useKbArticle(slug: string) {
  const asUser = useViewAsId()
  return useQuery({
    queryKey: kbKeys.bySlug(slug, asUser),
    queryFn: () => api<KbArticle>(`/kb-articles/${slug}`, { query: lens(asUser) }),
    enabled: Boolean(slug),
  })
}

// Admin only: the API rejects anyone else, so callers gate it with `enabled`.
export function useKbFeatureFlags({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: kbKeys.featureFlags(),
    queryFn: () => api<KbFeatureFlag[]>('/kb-articles/feature-flags'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

// Only the collections the viewer may see, in display order.
export function useKbCollections() {
  const asUser = useViewAsId()
  return useQuery({
    queryKey: kbKeys.collections(asUser),
    queryFn: async () => (await api<{ data: KbCollection[] }>('/kb-collections', { query: lens(asUser) })).data,
  })
}

export function useKbCollection(slug: string) {
  const asUser = useViewAsId()
  return useQuery({
    queryKey: kbKeys.collection(slug, asUser),
    queryFn: () => api<KbCollection>(`/kb-collections/${slug}`, { query: lens(asUser) }),
    enabled: Boolean(slug),
  })
}

export type KbCollectionInput = Pick<KbCollection, 'name' | 'description' | 'order'> & { companyIds: string[]; featureFlags: string[] }

export function useSaveKbCollection(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbCollectionInput>) =>
      api<KbCollection>(id ? `/kb-collections/${id}` : '/kb-collections', { method: id ? 'PATCH' : 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useDeleteKbCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/kb-collections/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useCreateKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>('/kb-articles', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUpdateKbArticle(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>(`/kb-articles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useDeleteKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/kb-articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUploadKbImage(articleId?: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      const path = articleId ? `/kb-articles/${articleId}/images` : '/kb-articles/images'
      return api<KbImageUploadResponse>(path, { method: 'POST', body: fd })
    },
  })
}

// Staff only: an article's saved versions, newest first, without their text.
export function useKbRevisions(articleId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: kbKeys.revisions(articleId),
    // The latest revisions (the API lists up to 100) and how many there are in all.
    queryFn: () => api<{ data: KbRevision[]; total: number }>(`/kb-articles/${articleId}/revisions`),
    enabled,
  })
}

export function useKbRevision(articleId: string, revisionId: string | undefined) {
  return useQuery({
    queryKey: kbKeys.revision(articleId, revisionId ?? ''),
    queryFn: () => api<KbRevision>(`/kb-articles/${articleId}/revisions/${revisionId}`),
    enabled: Boolean(revisionId),
    staleTime: Infinity,
  })
}

export function useMarkKbReviewed(articleId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<KbArticle>(`/kb-articles/${articleId}/review`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useRevertKbArticle(articleId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (revisionId: string) => api<KbArticle>(`/kb-articles/${articleId}/revert/${revisionId}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}
